/**
 * useRecentAreas Hook
 *
 * Manages recently viewed areas in localStorage. Automatically deduplicates
 * and keeps the 10 most recent areas.
 *
 * @returns Object with recentAreas array and methods to add/clear areas
 *
 * @example
 * ```tsx
 * const { recentAreas, addRecentArea, clearRecentAreas } = useRecentAreas();
 *
 * // Add an area when user views it
 * addRecentArea({
 *   resourceId: 'berkeley-ca',
 *   name: 'Berkeley',
 *   city: 'Berkeley',
 *   state: 'CA'
 * });
 * ```
 */

import { useCallback } from 'react';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import { useLocalStorage } from '@/src/lib/hooks/utils';

const MAX_RECENT_AREAS = 10;
export const RECENT_AREAS_STORAGE_KEY = 'hotpads_recent_areas';

export type RecentArea = AutocompleteResult & {
  viewedAt?: number; // Timestamp when area was viewed
};

function withRecentArea(prev: Array<RecentArea>, area: AutocompleteResult): Array<RecentArea> {
  const filtered = prev.filter((item) => item.resourceId !== area.resourceId);
  return [{ ...area, viewedAt: Date.now() }, ...filtered].slice(0, MAX_RECENT_AREAS);
}

/** Records a viewed area outside React; a no-op when it is already the newest entry. */
export function recordRecentArea(area: AreaResponse) {
  if (!area.resourceId || !area.name) return;
  try {
    const prev: Array<RecentArea> = JSON.parse(window.localStorage.getItem(RECENT_AREAS_STORAGE_KEY) ?? '[]');
    if (prev[0]?.resourceId === area.resourceId) return;
    const entry: AutocompleteResult = {
      type: area.type ?? 'city',
      name: area.name,
      resourceId: area.resourceId,
      city: area.city,
      county: area.county,
      state: area.state,
    };
    const newValue = JSON.stringify(withRecentArea(prev, entry));
    window.localStorage.setItem(RECENT_AREAS_STORAGE_KEY, newValue);
    // The browser fires `storage` only in other tabs; the persistent search bars in this tab need it too.
    window.dispatchEvent(new StorageEvent('storage', { key: RECENT_AREAS_STORAGE_KEY, newValue }));
  } catch {
    // localStorage unavailable or invalid JSON — recording is best effort
  }
}

export function useRecentAreas() {
  const [recentAreas, setRecentAreas] = useLocalStorage<Array<RecentArea>>(RECENT_AREAS_STORAGE_KEY, []);

  // Add an area to recent areas (or update its timestamp if already exists)
  const addRecentArea = useCallback(
    (area: AutocompleteResult) => {
      setRecentAreas((prev) => withRecentArea(prev, area));
    },
    [setRecentAreas],
  );

  // Clear all recent areas
  const clearRecentAreas = useCallback(() => {
    setRecentAreas([]);
  }, [setRecentAreas]);

  return {
    recentAreas,
    addRecentArea,
    clearRecentAreas,
  };
}
